import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "412",
  "slug": "wettest-places-on-earth",
  "title": "The Wettest Places on Earth",
  "excerpt": "Some corners of the planet measure their rainfall not in millimetres but in metres a year. Here are the wettest places on Earth and the geography that drowns them.",
  "date": "2026-06-12",
  "category": "Geography",
  "image": "https://images.unsplash.com/photo-1583470790878-4f4f3811a01f?ixid=M3w5NjY4NzN8MHwxfHNlYXJjaHwxfHxyYWluZm9yZXN0JTIwcmFpbiUyMG1pc3R8ZW58MXwwfHx8MTc4MTI1MjMwMHww&ixlib=rb-4.1.0&w=800&q=80&fit=crop",
  "readTime": "4 min",
  "author": "EarthGuessr Team",
  "tldr": "The wettest inhabited places on Earth are Mawsynram and Cherrapunji in north-east India and the Choco region of Colombia, where annual rainfall averages well over 10 metres. Almost all of them sit where moist ocean winds are forced up against mountains.",
  "content": [
    {
      "type": "paragraph",
      "text": "In most of the world, a rainy year means a metre of rain or less. In a handful of places, a metre of rain is roughly what falls in a single wet month. These are the wettest places on Earth, where rainfall is measured in metres rather than millimetres and where the landscape, the architecture, and daily life are all shaped by water falling out of the sky for much of the year."
    },
    {
      "type": "paragraph",
      "text": "Extreme rainfall is not random. It clusters in a few specific geographic situations, and once you understand the recipe you can usually predict where the rainiest places will be before you ever look up a number."
    },
    {
      "type": "heading",
      "text": "What makes a place this wet"
    },
    {
      "type": "paragraph",
      "text": "The single most important ingredient is a mountain standing in the path of moist ocean air. When wind carrying water vapour hits high ground, it is forced upward. Rising air cools, the vapour condenses, and it falls as rain on the windward slope. This is called orographic rainfall, and it is responsible for almost every record on this list."
    },
    {
      "type": "paragraph",
      "text": "Add a seasonal monsoon or a position near the equator, where warm seas feed a constant supply of humid air, and you get the wettest conditions on the planet. The opposite side of those same mountains is often bone dry, sitting in what is called a rain shadow. The wettest place and one of the driest can be only a few dozen kilometres apart, separated by a single ridge."
    },
    {
      "type": "heading",
      "text": "Mawsynram and Cherrapunji, India"
    },
    {
      "type": "paragraph",
      "text": "The hills of Meghalaya in north-east India are the textbook example. The villages of Mawsynram and Cherrapunji (Sohra) sit on south-facing slopes that catch the full force of the South Asian summer monsoon as it sweeps north off the Bay of Bengal. Mawsynram is widely cited as the wettest inhabited place on Earth, with an annual average around 11,800 millimetres, and Cherrapunji holds some of the most extreme rainfall records ever measured, including roughly 26,000 millimetres in the single year of 1860 to 1861."
    },
    {
      "type": "paragraph",
      "text": "Nearly all of that rain arrives in a few intense monsoon months, then largely stops, so these are not places of constant drizzle but of overwhelming seasonal deluge. Local people have long used umbrellas woven from bamboo and banana leaf, and the region is famous for its living root bridges, grown over decades from the roots of rubber fig trees to survive floods that would wash away anything built of wood."
    },
    {
      "type": "heading",
      "text": "The Choco, Colombia"
    },
    {
      "type": "paragraph",
      "text": "If Meghalaya is the wettest by record, the Choco region on Colombia's Pacific coast may be the wettest by sheer consistency. Towns such as Lloro, Tutunendo, and Lopez de Micay regularly post annual averages above 12,000 millimetres, and unlike the monsoon belt the rain here falls almost every day, all year round."
    },
    {
      "type": "paragraph",
      "text": "The Choco sits where warm, humid Pacific air meets the western wall of the Andes, right under the band of rising air that circles the equator. There is no real dry season to interrupt it. The result is one of the rainiest and most biodiverse rainforests on the planet, a place where the line between river, swamp, and forest almost disappears."
    },
    {
      "type": "heading",
      "text": "Mountains that catch the sea wind"
    },
    {
      "type": "paragraph",
      "text": "The same recipe produces other contenders around the world, each one a tall barrier facing a warm ocean:"
    },
    {
      "type": "list",
      "items": [
        "Mount Waialeale on Kauai, Hawaii, receives around 9,500 millimetres a year as steady trade winds are forced straight up its steep flanks, while the leeward side of the same island stays dry enough for a desert canyon.",
        "Debundscha in Cameroon, at the foot of Mount Cameroon, gets roughly 10,000 millimetres where the Atlantic monsoon slams into a volcanic peak rising straight from the sea.",
        "The west coast of New Zealand's South Island and parts of southern Chile see huge totals as the roaring westerly winds pile up against coastal mountains."
      ]
    },
    {
      "type": "heading",
      "text": "How the wettest places look from above"
    },
    {
      "type": "paragraph",
      "text": "From satellite imagery, the wettest regions tend to share a look: deep, saturated green vegetation, frequent cloud cover that can make them genuinely hard to photograph, and steep terrain laced with countless streams and rivers. Persistent cloud is itself a clue. When a patch of the map is hard to see because it is so often hidden under cloud, you may be looking at one of the rainiest places on Earth."
    }
  ]
};
